import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Star, Languages, ChevronLeft, ClipboardPaste, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { author: "", rating: 5, comment: "", date: "" };

export default function PropertyReviews() {
  const { id } = useParams();
  const [info, setInfo] = useState({ name: "", slug: "", reviews: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [translating, setTranslating] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);

  const load = () => api.get(`/admin/properties/${id}/reviews`).then((r) => setInfo(r.data));
  useEffect(() => { load(); }, [id]);

  const doImport = async () => {
    if (!importText.trim()) { toast.error("Incolla il testo delle recensioni"); return; }
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/properties/${id}/reviews/import`, { raw_text: importText });
      toast.success(`${data.added} recensioni importate`);
      setImportOpen(false);
      setImportText("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const translateAll = async () => {
    setTranslatingAll(true);
    try {
      const { data } = await api.post(`/admin/properties/${id}/reviews/translate-all?lang=en`);
      toast.success(data.translated > 0 ? `${data.translated} recensioni tradotte in EN` : "Tutte le recensioni sono già tradotte");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTranslatingAll(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ author: r.author, rating: r.rating, comment: r.comment, date: r.date || "" }); setOpen(true); };

  const save = async () => {
    if (!form.author || !form.comment) { toast.error("Autore e testo sono obbligatori"); return; }
    try {
      const payload = { author: form.author, rating: Number(form.rating), comment: form.comment, date: form.date };
      if (editing) await api.put(`/admin/properties/${id}/reviews/${editing.rid}`, payload);
      else await api.post(`/admin/properties/${id}/reviews`, payload);
      toast.success("Recensione salvata");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (r) => {
    try { await api.delete(`/admin/properties/${id}/reviews/${r.rid}`); toast.success("Recensione eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const translate = async (r) => {
    setTranslating(r.rid);
    try { await api.post(`/admin/properties/${id}/reviews/${r.rid}/translate?lang=en`); toast.success("Tradotta in inglese"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTranslating(null); }
  };

  return (
    <AdminLayout title="Gestione recensioni">
      <div className="mb-6">
        <Link to="/admin/properties" className="text-sm text-brand-teal hover:underline flex items-center gap-1 mb-3"><ChevronLeft size={15} /> Torna alle strutture</Link>
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <p className="font-serif text-xl text-brand-plum">{info.name}</p>
            <p className="text-sm text-brand-plum/50">{info.reviews.length} recensioni · mostrate sul mini-sito</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={translateAll} disabled={translatingAll || info.reviews.length === 0} data-testid="reviews-translate-all-btn" variant="outline" className="rounded-full border-brand-teal/40 text-brand-tealDark hover:bg-brand-tealLight">
              <Languages size={16} className="mr-1.5" /> {translatingAll ? "Traduzione..." : "Traduci tutte in EN"}
            </Button>
            <Button onClick={() => setImportOpen(true)} data-testid="reviews-import-btn" variant="outline" className="rounded-full border-brand-plum/20 text-brand-plum hover:bg-brand-warm">
              <ClipboardPaste size={16} className="mr-1.5" /> Importa da Airbnb/Booking
            </Button>
            <Button onClick={openNew} data-testid="review-new-btn" className="rounded-full bg-brand-plum hover:bg-brand-violet text-white px-6"><Plus size={18} className="mr-1" /> Nuova recensione</Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2" data-testid="reviews-grid">
        {info.reviews.map((r) => (
          <div key={r.rid} className="rounded-2xl bg-white border border-brand-plum/8 shadow-sm p-5" data-testid={`review-card-${r.rid}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, s) => <Star key={s} size={13} className={s < Math.round(r.rating) ? "fill-brand-gold text-brand-gold" : "text-brand-plum/20"} />)}
                  <span className="text-xs text-brand-plum/50 ml-1">{r.rating}</span>
                </div>
                <p className="text-sm font-medium text-brand-plum mt-1">{r.author} <span className="text-brand-plum/40 font-normal">· {r.date}</span></p>
              </div>
              <div className="flex gap-1">
                <Button onClick={() => openEdit(r)} variant="ghost" size="icon" className="text-brand-plum/60 hover:text-brand-plum h-8 w-8" data-testid={`review-edit-${r.rid}`}><Pencil size={15} /></Button>
                <Button onClick={() => remove(r)} variant="ghost" size="icon" className="text-red-500 hover:bg-red-50 h-8 w-8" data-testid={`review-delete-${r.rid}`}><Trash2 size={15} /></Button>
              </div>
            </div>
            <p className="text-sm text-brand-plum/70 italic mt-3">"{r.comment}"</p>
            {r.comment_en ? (
              <p className="text-sm text-brand-tealDark mt-2 pt-2 border-t border-brand-plum/8"><span className="text-[10px] uppercase font-semibold text-brand-teal">EN</span> "{r.comment_en}"</p>
            ) : (
              <Button onClick={() => translate(r)} disabled={translating === r.rid} variant="outline" size="sm" data-testid={`review-translate-${r.rid}`} className="mt-3 border-brand-teal/40 text-brand-tealDark hover:bg-brand-tealLight">
                <Languages size={14} className="mr-1.5" /> {translating === r.rid ? "Traduzione..." : "Traduci in EN"}
              </Button>
            )}
          </div>
        ))}
        {info.reviews.length === 0 && <p className="col-span-2 p-10 text-center text-brand-plum/40 bg-white rounded-2xl border border-brand-plum/8">Nessuna recensione. Aggiungine una per mostrarla sul mini-sito.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-plum">{editing ? "Modifica recensione" : "Nuova recensione"}</DialogTitle><DialogDescription>Le recensioni vengono mostrate sul mini-sito della struttura.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Autore</Label><Input value={form.author} onChange={(e) => set("author", e.target.value)} data-testid="review-author-input" className="mt-1" placeholder="Es. Sofia" /></div>
              <div><Label>Valutazione (1-5)</Label><Input type="number" min="1" max="5" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)} data-testid="review-rating-input" className="mt-1" /></div>
            </div>
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="review-date-input" className="mt-1" /></div>
            <div><Label>Testo recensione (italiano)</Label><Textarea rows={4} value={form.comment} onChange={(e) => set("comment", e.target.value)} data-testid="review-comment-input" className="mt-1" placeholder="Soggiorno perfetto..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="review-save-btn" className="bg-brand-plum text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" data-testid="review-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-plum">Importa recensioni</DialogTitle>
            <DialogDescription>Incolla il testo copiato da Airbnb, Booking.com o altre piattaforme. L'AI estrae autore, voto, testo e data automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea rows={9} value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="review-import-textarea" placeholder={"Es.\nSofia · settembre 2025\n★★★★★\nSoggiorno perfetto, casa curata e host disponibilissimo!\n\nThomas · agosto 2025\n9/10\nWunderschön! Alles war sauber und die Lage ist ideal."} className="mt-1 font-mono text-xs" />
            <p className="text-xs text-brand-plum/50 flex items-center gap-1.5"><Sparkles size={13} className="text-brand-teal" /> Le recensioni importate mantengono la lingua originale; usa "Traduci tutte in EN" per la versione inglese.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Annulla</Button>
            <Button onClick={doImport} disabled={importing} data-testid="review-import-submit" className="bg-brand-plum text-white">
              <Sparkles size={15} className="mr-1.5" /> {importing ? "Elaborazione..." : "Importa con AI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
